<template>
	<div class="ir">
		<div class="ir-chart">
			<div class="ir-legend">
				<span v-for="(a, codice) in AREE" :key="codice" class="ir-legend-item"> <span class="ir-dot" :style="{ background: COLORI[codice] }"></span>{{ a.label }} </span>
				<span class="ir-legend-item"><span class="ir-square"></span>Vittorie rapide: poco sforzo, tanto valore</span>
				<span class="ir-muted">dimensione = priorità</span>
			</div>
			<svg :viewBox="`0 0 ${W} ${H}`" class="ir-svg" role="img" aria-label="Radar delle opportunità: valore stimato contro sforzo">
				<!-- quadrante delle vittorie rapide: poco sforzo, tanto valore -->
				<rect :x="PAD_L" :y="PAD_T" :width="(W - PAD_L - PAD_R) / 3" :height="(H - PAD_T - PAD_B) / 2" class="ir-quick" />

				<line v-for="t in ticks" :key="'t' + t.value" :x1="PAD_L" :x2="W - PAD_R" :y1="t.y" :y2="t.y" class="ir-grid" />
				<text v-for="t in ticks" :key="'l' + t.value" :x="PAD_L - 6" :y="t.y + 4" class="ir-axis" text-anchor="end">{{ t.label }}</text>

				<line v-for="i in 2" :key="'b' + i" :x1="bandX(i)" :x2="bandX(i)" :y1="PAD_T" :y2="H - PAD_B" class="ir-band" />
				<text v-for="(s, i) in SFORZI" :key="'s' + i" :x="bandCenter(i + 1)" :y="H - PAD_B + 20" class="ir-axis" text-anchor="middle">{{ s }}</text>
				<text :x="(W + PAD_L - PAD_R) / 2" :y="H - 6" class="ir-axis-title" text-anchor="middle">Sforzo</text>
				<text :x="14" :y="(H - PAD_B + PAD_T) / 2" class="ir-axis-title" text-anchor="middle" :transform="`rotate(-90 14 ${(H - PAD_B + PAD_T) / 2})`">Valore stimato</text>

				<g v-for="p in punti" :key="p.item._id" class="ir-point" :class="{ 'ir-point--sel': selected && selected._id === p.item._id }" @click="selected = p.item">
					<circle :cx="p.x" :cy="p.y" :r="p.r" :fill="p.colore" />
					<title>{{ p.item.title }} · {{ euro(p.item.impact) }}</title>
				</g>
			</svg>
			<div v-if="!items.length" class="ir-empty">Nessun consiglio da fare: il radar è vuoto.</div>
		</div>

		<div class="ir-side">
			<template v-if="selected">
				<div class="d-flex align-center">
					<v-chip x-small label outlined :color="area(selected.area).colore">{{ area(selected.area).label }}</v-chip>
					<v-spacer></v-spacer>
					<InsightRowMenu :item="selected" @open="$emit('open', $event)" @status="onStatus" />
				</div>
				<div class="ir-title mt-2">{{ selected.title }}</div>
				<div class="ir-text">{{ selected.text }}</div>
				<div class="ir-value">{{ euro(selected.impact) }} <span class="ir-muted">stimati</span></div>
				<v-btn v-if="selected.action && selected.action.type" color="primary" small depressed class="mt-3" @click="$emit('open', { item: selected })">{{ selected.action.label || 'Apri' }}</v-btn>
			</template>
			<div v-else class="ir-muted">
				Ogni bolla è un consiglio da fare. In alto a sinistra ci sono le vittorie rapide: valgono molto e richiedono poco lavoro. Clicca una bolla per vederla e agire.
			</div>
		</div>
	</div>
</template>

<script>
import InsightRowMenu from './InsightRowMenu.vue';
import { AREE, area, euro } from './formato';

// colori esadecimali delle aree (le stesse tinte dei chip Vuetify)
const COLORI = { magazzino: '#3f51b5', prezzo: '#ff5722', clienti: '#009688', marketing: '#9c27b0', seo: '#607d8b' };
const SFORZI = ['Un clic', 'Qualche minuto', 'Lavoro vero'];

// posizione stabile nella fascia: stesso consiglio, stesso punto a ogni caricamento
// FNV-1a: gli ObjectId creati nello stesso giro sono consecutivi, un hash semplice li metterebbe in colonna
const jitter = id => {
	let h = 2166136261;
	for (const c of String(id)) h = Math.imul(h ^ c.charCodeAt(0), 16777619) >>> 0;
	return (h % 1000) / 1000 - 0.5;
};

/**
 * Radar delle opportunita': valore stimato (scala logaritmica) contro sforzo, dalla stessa fonte della lista.
 * Clic su una bolla = dettaglio e azioni a destra.
 */
export default {
	name: 'InsightsRadar',
	components: { InsightRowMenu },
	props: {
		items: { type: Array, default: () => [] },
	},
	data() {
		return { W: 760, H: 420, PAD_L: 64, PAD_R: 16, PAD_T: 20, PAD_B: 44, selected: null, AREE, COLORI, SFORZI };
	},
	computed: {
		maxLog() {
			const max = Math.max(10, ...this.items.map(i => i.impact || 0));
			return Math.log10(max + 1);
		},
		ticks() {
			const out = [];
			for (let v = 1; Math.log10(v + 1) <= this.maxLog + 0.01; v *= 10) out.push({ value: v, y: this.yOf(v), label: euro(v) });
			return out;
		},
		punti() {
			// le bolle piccole sopra le grandi, cosi' restano cliccabili
			return [...this.items]
				.sort((a, b) => (b.severity || 1) - (a.severity || 1))
				.map(item => {
					const band = Math.min(3, Math.max(1, item.effort || 1));
					const w = (this.W - this.PAD_L - this.PAD_R) / 3;
					const r = 5 + (item.severity || 1) * 4;
					// la bolla resta tutta dentro l'area del grafico
					const y = Math.min(this.H - this.PAD_B - r, Math.max(this.PAD_T + r, this.yOf(item.impact || 0)));
					return {
						item,
						x: this.bandCenter(band) + jitter(item._id) * w * 0.75,
						y,
						r,
						colore: COLORI[item.area] || '#9e9e9e',
					};
				});
		},
	},
	watch: {
		items(list) {
			if (this.selected && !list.find(i => i._id === this.selected._id)) this.selected = null;
		},
	},
	methods: {
		area,
		euro,
		yOf(v) {
			const h = this.H - this.PAD_T - this.PAD_B;
			return this.PAD_T + h - (Math.log10((v || 0) + 1) / this.maxLog) * h;
		},
		bandX(i) {
			return this.PAD_L + ((this.W - this.PAD_L - this.PAD_R) / 3) * i;
		},
		bandCenter(band) {
			return this.PAD_L + ((this.W - this.PAD_L - this.PAD_R) / 3) * (band - 0.5);
		},
		onStatus(e) {
			this.selected = null;
			this.$emit('status', e);
		},
	},
};
</script>

<style scoped>
.ir {
	display: grid;
	grid-template-columns: minmax(0, 1fr) 300px;
	gap: 16px;
}
@media (max-width: 960px) {
	.ir {
		grid-template-columns: 1fr;
	}
}
.ir-chart {
	position: relative;
	min-width: 0;
}
.ir-svg {
	width: 100%;
	height: auto;
	display: block;
}
.ir-legend {
	display: flex;
	flex-wrap: wrap;
	gap: 4px 14px;
	font-size: 12px;
	color: #1f2d3d;
	margin-bottom: 6px;
}
.ir-legend-item {
	display: inline-flex;
	align-items: center;
	gap: 6px;
}
.ir-dot {
	width: 10px;
	height: 10px;
	border-radius: 50%;
	display: inline-block;
}
.ir-quick {
	fill: #e8f5e9;
}
.ir-square {
	width: 12px;
	height: 10px;
	background: #e8f5e9;
	border: 1px solid #a5d6a7;
	display: inline-block;
}
.ir-grid {
	stroke: #eef1f5;
}
.ir-band {
	stroke: #dfe4ea;
	stroke-dasharray: 4 4;
}
.ir-axis {
	font-size: 11px;
	fill: #5f6b7a;
}
.ir-axis-title {
	font-size: 11px;
	fill: #5f6b7a;
	font-weight: 600;
}
.ir-point {
	cursor: pointer;
}
.ir-point circle {
	fill-opacity: 0.75;
	stroke: #fff;
	stroke-width: 1.5;
}
.ir-point:hover circle,
.ir-point--sel circle {
	fill-opacity: 1;
	stroke: #1f2d3d;
	stroke-width: 2;
}
.ir-empty {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #5f6b7a;
	font-size: 13px;
}
.ir-side {
	border: 1px solid #e3e8ef;
	border-radius: 8px;
	padding: 14px;
	background: #fff;
	align-self: start;
}
.ir-title {
	font-weight: 600;
	color: #1f2d3d;
}
.ir-text {
	font-size: 13px;
	color: #1f2d3d;
	margin-top: 4px;
}
.ir-value {
	font-size: 18px;
	font-weight: 600;
	margin-top: 8px;
	color: #1f2d3d;
}
.ir-muted {
	font-size: 12px;
	color: #5f6b7a;
	font-weight: 400;
}
</style>
